import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { storageCache } from '@services/cache';

import SimpleButton from '@components/SimpleButton';
import LateralSpace from '@components/LateralSpace';
import SeparatorLine from '@components/SeparatorLine/index';
import {
  ROUTES,
  getCartProductConfigurationRoute,
} from '@services/routes.service';
import ShopCard from '@conversion/containers/ShopCard';
import Shell from '@shared/containers/Shell';
import FixedHeader from '@components/FixedHeader';
import IntersectionObserver from '@components/IntersectionObserver';
import { addCurrencySymbol } from '@utils/currency/addSymbol';
import { Toast, MESSAGE_TYPE } from '@components/Messages';
import { DNI_USER_VALID_STORAGE } from '@core/reducers/cart/constants';
import { DELETE_METHOD } from '@conversion/tracking/constants';
import DonationWarning from '@cart/containers/DonationWarning';
import Observer from '@utils/browser/intersectionObserver';
import { FLAGS, useFlagVariation } from '@providers/FwFProvider';

import CartPrice from '../../components/CartPrice';
import cartMessages from './messages';
import CartList from '../CartList';
import AgeValidationReliable from '../AgeValidationReliable/AgeValidationReliable';

import Detail from './components/Detail';
import CartRow from './components/CartRow';
import FooterDefault from './components/Footer';
import Content from './components/Content';
import ShopCardWrapper from './components/ShopCardWrapper';
import FoodButtonWrapper from './components/FoodButtonWrapper';
import FooterWrapper from './components/FooterWrapper';
import DesktopCart from './components/DesktopCart/Loadable';
import CartListTitle from './components/CartListTitle';

const Cart = props => {
  const {
    cartSubtotal,
    cartItems,
    cartDiscounts,
    needsToAdd,
    currencySymbol,
    isUserLogged,
    trackCheckoutClicked,
    trackModalOpened,
    trackModalClosed,
    areOnlyDonationsItemsInCart,
    businessType,
    subHeader,
    showPhoneVerificationModal,
    Footer = FooterDefault,
    isDesktopLayout,
    cartItemsRequireValidate,
    showErrorToast,
    isUpdatingLines,
    shopId,
    cartId,
    searchedLocation,
    countryShortName,
    shop,
    redirectToShopDetails,
    goBack,
    redirect,
    pushPage,
    fetchProductsRequireValidate,
    trackToastShown,
    deleteLines,
    shopMaxProducts,
    cartTotalProductsQuantity,
    trackAddMoreFoodClicked,
    trackCartDeleted,
    intl,
    userTotalOrders,
  } = props;
  const [editMode, setEditMode] = useState({
    on: false,
    itemsToRemove: [],
    isDeletingLines: false,
  });
  const [isShopCardVisible, setIsShopCardVisible] = useState(true);
  const [openCheckDniModal, setOpenCheckDniModal] = useState(false);
  const [itemsRequireValidate, setItemsRequireValidate] = useState([]);
  const [
    verifyingProductsValidation,
    setVerifyingProductsValidation,
  ] = useState(false);
  const [toastErrorRetryValidateAge, setToastErrorRetryValidateAge] = useState(
    false,
  );

  const onClose = () => {
    if (redirectToShopDetails) redirect(shop.url);
    else goBack();
  };

  const onAddMoreFoodClick = () => {
    onClose();
    trackAddMoreFoodClicked();
  };

  const onEditMode = () => {
    setEditMode(state => ({ ...state, on: !state.on, itemsToRemove: [] }));
  };

  const onItemChecked = item => {
    setEditMode(state => {
      let selected = [...state.itemsToRemove];
      if (selected.some(x => x === item.guid))
        selected = selected.filter(x => x !== item.guid);
      else selected.push(item.guid);
      return { ...state, itemsToRemove: selected };
    });
  };

  const onItemClicked = item => {
    pushPage(getCartProductConfigurationRoute({ lineId: item.guid }));
  };

  const onDeleteLinesFinish = () => {
    const shouldGoBack = !cartItems.length && !isDesktopLayout;
    if (shouldGoBack) {
      goBack();
      trackCartDeleted(DELETE_METHOD.LAST_PRODUCT);
    }
    setEditMode(state => ({
      ...state,
      isDeletingLines: false,
      itemsToRemove: [],
    }));
  };

  const onConfirmProductsDeletion = () => {
    if (editMode.itemsToRemove.length > 0) {
      setEditMode(state => ({ ...state, isDeletingLines: true }));
      deleteLines({ lines: editMode.itemsToRemove }).then(() => {
        onDeleteLinesFinish();
      });
    }
  };

  const checkProductsRequireValidate = async () => {
    const products = [];
    setVerifyingProductsValidation(true);
    setToastErrorRetryValidateAge(false);
    cartItems.forEach(i => {
      products.push(i.product);
    });
    const response = await fetchProductsRequireValidate(products, shop.id);
    if (response.need_reliable_age_validation) {
      setOpenCheckDniModal(true);
      setVerifyingProductsValidation(false);
    } else {
      pushPage(ROUTES.CHECKOUT);
    }
  };

  const checkShopLimit = async () => {
    const maxCartLimitReached =
      shopMaxProducts && cartTotalProductsQuantity > shopMaxProducts;
    if (maxCartLimitReached) {
      trackToastShown({
        screenName: 'cart',
        screenType: 'cart',
        toastType: 'max_cart_limit_reached',
      });
      const title = intl.formatMessage(cartMessages.cart_shop_limit_products, {
        shopMaxProducts,
      });
      showErrorToast({
        title,
      });
      return true;
    }
    return false;
  };

  const validateProductsBeforeContinue = async () => {
    const checklimit = await checkShopLimit();
    const productsNeedAgeValidation =
      !checklimit && !storageCache.get(DNI_USER_VALID_STORAGE);
    if (productsNeedAgeValidation) {
      checkProductsRequireValidate();
    } else if (!checklimit) {
      pushPage(ROUTES.CHECKOUT);
    }
  };

  const showErrorToastRetry = () => {
    setToastErrorRetryValidateAge(true);
  };

  const needsToAddMsg = formattedNeedsToAdd => {
    if (needsToAdd > 0) {
      return intl.formatMessage(
        cartMessages.cart_add_more_items_to_reach_minimum,
        {
          needsToAdd: formattedNeedsToAdd,
        },
      );
    }
    return intl.formatMessage(cartMessages.cart_add_more_items);
  };

  const intersectionCallback = entry => {
    const visible = Observer.elementIsVisible(entry);
    setIsShopCardVisible(visible);
  };

  const getLabelHeader = () => {
    const actionLabel = editMode.on
      ? intl.formatMessage(cartMessages.cart_close_edit_mode)
      : intl.formatMessage(cartMessages.cart_open_edit_mode);
    return actionLabel;
  };

  const formattedNeedsToAdd = addCurrencySymbol(
    intl.formatNumber(needsToAdd),
    currencySymbol,
  );

  const needsToAddItems = needsToAdd > 0;
  const hasItemsInCart = cartItems?.length > 0;
  const THRESHOLD_MARGIN = '-42px'; // It must be the same value that the margin top in "<Content/>"
  const showNativeModal = useFlagVariation(FLAGS.WEB_TO_NATIVE_CHECKOUT);

  const isNewUser = useMemo(() => {
    const { totalOrders = -1 } = userTotalOrders || {};
    return totalOrders === 0;
  }, [userTotalOrders]);

  if (!isDesktopLayout) {
    return (
      <Shell>
        <FixedHeader
          label={intl.formatMessage(cartMessages.cart_title)}
          actionLabel={getLabelHeader()}
          onActionClick={onEditMode}
          onClose={onClose}
          isFixed
          shadow={!isShopCardVisible}
        />

        <Content>
          <Detail>
            <ShopCardWrapper>
              <IntersectionObserver
                callback={intersectionCallback}
                thresholdMargin={THRESHOLD_MARGIN}
                onInAndOut
              >
                <ShopCard isCart />
              </IntersectionObserver>
            </ShopCardWrapper>
            {subHeader}
            {hasItemsInCart && (
              <CartListTitle>
                {intl.formatMessage(cartMessages.cart_list_title)}
              </CartListTitle>
            )}
            <CartList
              items={cartItems}
              isEditing={editMode.on}
              itemsSelected={editMode.itemsToRemove}
              onItemChecked={onItemChecked}
              onItemClicked={onItemClicked}
              cartItemsRequireValidate={itemsRequireValidate}
              isUpdatingLines={isUpdatingLines}
            />

            <LateralSpace left right>
              <SeparatorLine />
              <CartPrice subtotal={cartSubtotal} discounts={cartDiscounts} />
            </LateralSpace>
          </Detail>
          <DonationWarning />
          {needsToAddItems && (
            <CartRow>
              <FoodButtonWrapper needsToAddItems={needsToAddItems}>
                <SimpleButton
                  elementId="cart__additems__action"
                  label={needsToAddMsg(formattedNeedsToAdd)}
                  onClick={onAddMoreFoodClick}
                />
              </FoodButtonWrapper>
            </CartRow>
          )}
        </Content>
        {toastErrorRetryValidateAge && (
          <Toast
            type={MESSAGE_TYPE.NEGATIVE}
            title={intl.formatMessage(cartMessages.error_retry)}
            actionLabel={intl.formatMessage(
              cartMessages.error_retry_toast_action,
            )}
            onCallToAction={() => {
              setOpenCheckDniModal(true);
              setToastErrorRetryValidateAge(false);
            }}
          />
        )}
        <FooterWrapper>
          <Footer
            trackCheckoutClicked={trackCheckoutClicked}
            trackModalOpened={trackModalOpened}
            trackModalClosed={trackModalClosed}
            editMode={editMode}
            needsToAdd={needsToAdd}
            onConfirmProductsDeletion={onConfirmProductsDeletion}
            areOnlyDonationsItemsInCart={areOnlyDonationsItemsInCart}
            showPhoneVerificationModal={showPhoneVerificationModal}
            verifyingProductsValidation={verifyingProductsValidation}
            isUpdatingLines={isUpdatingLines}
            validateProcucts={validateProductsBeforeContinue}
            cartId={cartId}
            shopId={shopId}
            searchedLocation={searchedLocation}
            showNativeModal={showNativeModal}
            isNewUser={isNewUser}
          />
        </FooterWrapper>
        {openCheckDniModal && (
          <AgeValidationReliable
            open
            onAccept={() => {
              setItemsRequireValidate(cartItemsRequireValidate);
              setOpenCheckDniModal(false);
              showErrorToast({
                title: intl.formatMessage(cartMessages.remove_products),
              });
            }}
            onClose={() => {
              setOpenCheckDniModal(false);
            }}
            showErrorToastRetry={showErrorToastRetry}
          />
        )}
      </Shell>
    );
  }

  return (
    <DesktopCart
      subHeader={subHeader}
      cartItems={cartItems}
      onItemChecked={onItemChecked}
      onItemClicked={onItemClicked}
      isUserLogged={isUserLogged}
      cartSubtotal={cartSubtotal}
      cartDiscounts={cartDiscounts}
      businessType={businessType}
      labelNeedToAdd={needsToAddMsg(formattedNeedsToAdd)}
      trackCheckoutClicked={trackCheckoutClicked}
      trackModalOpened={trackModalOpened}
      trackModalClosed={trackModalClosed}
      needsToAdd={needsToAdd}
      onConfirmProductsDeletion={onConfirmProductsDeletion}
      areOnlyDonationsItemsInCart={areOnlyDonationsItemsInCart}
      itemsToremove={editMode.itemsToRemove}
      isUpdatingLines={isUpdatingLines}
      showNativeModal={showNativeModal}
      cartId={cartId}
      shopId={shopId}
      searchedLocation={searchedLocation}
      isNewUser={isNewUser}
      countryShortName={countryShortName}
      validateProcucts={validateProductsBeforeContinue}
    />
  );
};

Cart.propTypes = {
  // data
  shop: PropTypes.object.isRequired,
  cartSubtotal: PropTypes.number.isRequired,
  cartItems: PropTypes.array.isRequired,
  cartItemsRequireValidate: PropTypes.array,
  cartDiscounts: PropTypes.array,
  needsToAdd: PropTypes.number.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  redirectToShopDetails: PropTypes.bool.isRequired,
  isUserLogged: PropTypes.bool.isRequired,
  areOnlyDonationsItemsInCart: PropTypes.bool.isRequired,
  businessType: PropTypes.string.isRequired,
  isDesktopLayout: PropTypes.bool.isRequired,
  showPhoneVerificationModal: PropTypes.bool,
  isUpdatingLines: PropTypes.bool,
  shopMaxProducts: PropTypes.number,
  cartTotalProductsQuantity: PropTypes.number,
  shopId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  searchedLocation: PropTypes.object,
  countryShortName: PropTypes.string,
  userTotalOrders: PropTypes.object,

  // callbacks
  // intl eliminado, ya no es necesario con useIntl
  deleteLines: PropTypes.func.isRequired,
  pushPage: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  redirect: PropTypes.func.isRequired,
  fetchProductsRequireValidate: PropTypes.func.isRequired,
  showErrorToast: PropTypes.func.isRequired,
  trackToastShown: PropTypes.func.isRequired,

  // tracking
  trackCheckoutClicked: PropTypes.func.isRequired,
  trackModalOpened: PropTypes.func.isRequired,
  trackModalClosed: PropTypes.func.isRequired,
  trackAddMoreFoodClicked: PropTypes.func.isRequired,
  trackCartDeleted: PropTypes.func.isRequired,

  // Joker
  subHeader: PropTypes.node,
  Footer: PropTypes.object,
  intl: intlShape.isRequired,
};

Cart.defaultProps = { showPhoneVerificationModal: false };

export { Cart as CartTest };
export default injectIntl(Cart);
